import { hexDistance } from "../hex-path";
import { coordKey } from "../hex-path/hex-grid";
import type { HexCoord } from "../hex-path/types";

/** Rectangular tile footprint anchored at the top-left cell. */
export interface EnvironmentFootprint {
	width: number;
	height: number;
}

export const DEFAULT_ENVIRONMENT_FOOTPRINT: EnvironmentFootprint = {
	width: 1,
	height: 1,
};

export interface EnvironmentFootprintAnchor {
	row: number;
	col: number;
}

export interface EnvironmentFootprintPlacementContext {
	rows: number;
	cols: number;
	occupied: Set<string>;
	pathCells: HexCoord[];
	minRow: number;
	maxRow: number;
	minCol: number;
	maxCol: number;
	minDistanceToPath: number;
	maxDistanceToPath: number;
	minSpacing: number;
	existingPlacements: Array<{
		anchor: EnvironmentFootprintAnchor;
		footprint: EnvironmentFootprint;
	}>;
}

export function resolveEnvironmentFootprint(
	footprint?: Partial<EnvironmentFootprint>,
): EnvironmentFootprint {
	const width = Math.max(1, Math.floor(footprint?.width ?? 1));
	const height = Math.max(1, Math.floor(footprint?.height ?? 1));
	return { width, height };
}

export function listFootprintCells(
	anchor: EnvironmentFootprintAnchor,
	footprint: EnvironmentFootprint,
): HexCoord[] {
	const cells: HexCoord[] = [];
	for (let rowOffset = 0; rowOffset < footprint.height; rowOffset += 1) {
		for (let colOffset = 0; colOffset < footprint.width; colOffset += 1) {
			cells.push({
				row: anchor.row + rowOffset,
				col: anchor.col + colOffset,
			});
		}
	}
	return cells;
}

/** Cells on the rectangular perimeter (includes corners; 1×N uses all cells). */
export function listFootprintPerimeterCells(
	anchor: EnvironmentFootprintAnchor,
	footprint: EnvironmentFootprint,
): HexCoord[] {
	if (footprint.width <= 1 && footprint.height <= 1) {
		return listFootprintCells(anchor, footprint);
	}

	const perimeterKeys = new Set<string>();
	const maxRowOffset = footprint.height - 1;
	const maxColOffset = footprint.width - 1;

	for (let rowOffset = 0; rowOffset < footprint.height; rowOffset += 1) {
		for (let colOffset = 0; colOffset < footprint.width; colOffset += 1) {
			const isPerimeter =
				rowOffset === 0 ||
				rowOffset === maxRowOffset ||
				colOffset === 0 ||
				colOffset === maxColOffset;
			if (!isPerimeter) {
				continue;
			}
			perimeterKeys.add(
				coordKey(anchor.row + rowOffset, anchor.col + colOffset),
			);
		}
	}

	return [...perimeterKeys].map((key) => {
		const [row, col] = key.split(",").map(Number);
		return { row: row ?? 0, col: col ?? 0 };
	});
}

export function isFootprintWithinGrid(
	anchor: EnvironmentFootprintAnchor,
	footprint: EnvironmentFootprint,
	rows: number,
	cols: number,
): boolean {
	if (anchor.row < 0 || anchor.col < 0) {
		return false;
	}
	return (
		anchor.row + footprint.height <= rows &&
		anchor.col + footprint.width <= cols
	);
}

export function isFootprintInteriorAvailable(
	anchor: EnvironmentFootprintAnchor,
	footprint: EnvironmentFootprint,
	occupied: Set<string>,
): boolean {
	return listFootprintCells(anchor, footprint).every((cell) => {
		return !occupied.has(coordKey(cell.row, cell.col));
	});
}

function resolveDistanceToPath(cell: HexCoord, pathCells: HexCoord[]): number {
	if (pathCells.length === 0) {
		return Number.POSITIVE_INFINITY;
	}
	return pathCells.reduce((minDistance, pathCell) => {
		return Math.min(
			minDistance,
			hexDistance(cell.row, cell.col, pathCell.row, pathCell.col),
		);
	}, Number.POSITIVE_INFINITY);
}

export function isFootprintPerimeterWithinPathDistance(
	anchor: EnvironmentFootprintAnchor,
	footprint: EnvironmentFootprint,
	pathCells: HexCoord[],
	minDistanceToPath: number,
	maxDistanceToPath: number,
): boolean {
	return listFootprintPerimeterCells(anchor, footprint).every((cell) => {
		const distanceToPath = resolveDistanceToPath(cell, pathCells);
		return (
			distanceToPath >= minDistanceToPath &&
			distanceToPath <= maxDistanceToPath
		);
	});
}

function resolveFootprintSeparation(
	anchorA: EnvironmentFootprintAnchor,
	footprintA: EnvironmentFootprint,
	anchorB: EnvironmentFootprintAnchor,
	footprintB: EnvironmentFootprint,
): number {
	const cellsA = listFootprintCells(anchorA, footprintA);
	const cellsB = listFootprintCells(anchorB, footprintB);
	let minSeparation = Number.POSITIVE_INFINITY;
	for (const cellA of cellsA) {
		for (const cellB of cellsB) {
			minSeparation = Math.min(
				minSeparation,
				hexDistance(cellA.row, cellA.col, cellB.row, cellB.col),
			);
		}
	}
	return minSeparation;
}

export function isFootprintPerimeterSpacedFromPlacements(
	anchor: EnvironmentFootprintAnchor,
	footprint: EnvironmentFootprint,
	existingPlacements: EnvironmentFootprintPlacementContext["existingPlacements"],
	minSpacing: number,
): boolean {
	if (minSpacing <= 0 || existingPlacements.length === 0) {
		return true;
	}
	return existingPlacements.every((placement) => {
		return (
			resolveFootprintSeparation(
				anchor,
				footprint,
				placement.anchor,
				placement.footprint,
			) >= minSpacing
		);
	});
}

export function isValidEnvironmentFootprintPlacement(
	anchor: EnvironmentFootprintAnchor,
	footprint: EnvironmentFootprint,
	context: EnvironmentFootprintPlacementContext,
): boolean {
	if (
		anchor.row < context.minRow ||
		anchor.col < context.minCol ||
		anchor.row + footprint.height - 1 > context.maxRow ||
		anchor.col + footprint.width - 1 > context.maxCol
	) {
		return false;
	}
	if (!isFootprintWithinGrid(anchor, footprint, context.rows, context.cols)) {
		return false;
	}
	if (!isFootprintInteriorAvailable(anchor, footprint, context.occupied)) {
		return false;
	}
	if (
		!isFootprintPerimeterWithinPathDistance(
			anchor,
			footprint,
			context.pathCells,
			context.minDistanceToPath,
			context.maxDistanceToPath,
		)
	) {
		return false;
	}
	return isFootprintPerimeterSpacedFromPlacements(
		anchor,
		footprint,
		context.existingPlacements,
		context.minSpacing,
	);
}

export function occupyFootprintCells(
	anchor: EnvironmentFootprintAnchor,
	footprint: EnvironmentFootprint,
	occupied: Set<string>,
): void {
	for (const cell of listFootprintCells(anchor, footprint)) {
		occupied.add(coordKey(cell.row, cell.col));
	}
}

export function listFootprintAnchorCandidates(
	footprint: EnvironmentFootprint,
	context: Pick<
		EnvironmentFootprintPlacementContext,
		"minRow" | "maxRow" | "minCol" | "maxCol"
	>,
): EnvironmentFootprintAnchor[] {
	const candidates: EnvironmentFootprintAnchor[] = [];
	const maxAnchorRow = context.maxRow - footprint.height + 1;
	const maxAnchorCol = context.maxCol - footprint.width + 1;
	for (let row = context.minRow; row <= maxAnchorRow; row += 1) {
		for (let col = context.minCol; col <= maxAnchorCol; col += 1) {
			candidates.push({ row, col });
		}
	}
	return candidates;
}
